// This is a slice of state for the Document Service.
import { createSlice } from '@reduxjs/toolkit';

import { getDocumentURL, getParticipantURL } from '../api/commonHTTP';
import { DEFAULT_PAGE_LOAD_SIZE } from '../api/pagination';

import { apiCallBegan, apiDownload, apiUpload, apiVersion } from './api';

const slice = createSlice({
    name: 'documentService',
    initialState: {
        documentAuditData: [],
        documentDetailsSearch: [],
        documentDetailsSearchMetaData: {},
        documentUploadReturnData: [],
        participantDocumentDetails: [],
        participantDocumentDetailsMetaData: {},
        participantDocumentErrors: [],
        loadingDocumentAuditData: false,
        loadedDocumentAuditData: false,
        loadingDocumentDetailsSearch: false,
        loadingParticipantDocumentDetails: false,
        uploadErrorFiles: [],
        uploadErrors: [],
        errors: [],
        validationErrors: {},
        errorMessage: '',
        successMessage: '',
        version: {}
    },
    reducers: {
        documentAuditDataLoading: (state) => {
            state.loadingDocumentAuditData = true;
            state.documentAuditData = [];
        },

        documentAuditDataLoadingFailure: (state, action) => {
            state.loadingDocumentAuditData = false;
            state.documentAuditData = [];
            state.errorMessage = action.payload;
        },

        documentAuditDataLoaded: (state, action) => {
            state.loadingDocumentAuditData = false;
            state.loadedDocumentAuditData = true;
            state.documentAuditData = action.payload;
        },
        clearDocumentAuditData: (state) => {
            state.documentAuditData = [];
        },

        clearDocumentErrors: (state) => {
            state.uploadErrors = [];
            state.uploadErrorFiles = [];
            state.participantDocumentErrors = [];
        },

        documentDetailsSearchLoading: (state) => {
            state.loadingDocumentDetailsSearch = true;
        },
        documentDetailsSearchLoaded: (state, action) => {
            state.loadingDocumentDetailsSearch = false;
            const { content, ...rest } = action.payload;
            state.documentDetailsSearch = rest.first
                ? content
                : [...state.documentDetailsSearch, ...content];
            state.documentDetailsSearchMetaData = rest;
        },
        documentDetailsSearchLoadingFailure: (state, action) => {
            state.loadingDocumentDetailsSearch = false;
            state.errorMessage = action.payload;
        },
        clearDocumentDetailsSearch: (state) => {
            state.loadingDocumentDetailsSearch = false;
            state.documentDetailsSearch = [];
            state.documentDetailsSearchMetaData = {};
        },

        csvDocumentAdded: (state, action) => {
            if (!Array.isArray(action.payload)) return;
            const errors = action.payload?.filter((el) => el.errorCode);
            state.participantDocumentErrors = [...state.participantDocumentErrors, ...errors];
            state.uploadErrors = [...state.uploadErrors, ...errors];
        },

        addDocumentFailure: (state, action) => {
            state.uploadErrors = action.error;
        },

        addBatchDocumentFailure: (state, action) => {
            state.uploadErrorFiles = [...state.uploadErrorFiles, action.error];
        },

        participantDocumentDetailsLoading: (state) => {
            state.loadingParticipantDocumentDetails = true;
        },
        participantDocumentDetailsLoaded: (state, action) => {
            state.loadingParticipantDocumentDetails = false;
            const { content, ...rest } = action.payload;
            if (Array.isArray(content)) {
                state.participantDocumentDetails = rest.first
                    ? content
                    : [...state.participantDocumentDetails, ...content];
                state.participantDocumentDetailsMetaData = rest;
            }
        },
        participantDocumentDetailsLoadingFailed: (state, action) => {
            state.loadingParticipantDocumentDetails = false;
            state.errorMessage = action.payload;
        },

        participantDocumentDetailsUpdated: (state, action) => {
            state.participantDocumentDetails = state.participantDocumentDetails.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },
        participantDocumentDetailDeleted: (state, action) => {
            state.participantDocumentDetails = state.participantDocumentDetails.filter(
                (el) => el.id !== action.payload
            );
        },
        clearParticipantDocumentDetails: (state) => {
            state.participantDocumentDetails = [];
            state.participantDocumentDetailsMetaData = {};
        },

        participantDocumentsAdded: (state, action) => {
            state.documentUploadReturnData = action.payload;
            const errors = action.payload.filter((el) => el.errorCode);
            state.participantDocumentErrors = [...state.participantDocumentErrors, ...errors];
        },
        clearDocumentUploadReturnData: (state) => {
            state.documentUploadReturnData = [];
        },

        fileUploadErrorEntryDeleted: (state, action) => {
            state.participantDocumentErrors = state.participantDocumentErrors.filter(
                (el) => el.filename !== action.payload
            );
        },

        deleteFileUploadErrorEntries: (state) => {
            state.participantDocumentErrors = [];
        },

        errorOccurred: (state, action) => {
            state.errorMessage = action.payload;
        },

        validationErrorOccurred: (state, action) => {
            state.validationErrors = Object.entries(action.error).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: { error: true, message: value }
                }),
                {}
            );
        },

        clearValidationErrors: (state) => {
            state.validationErrors = {};
        },

        errorMessageSet: (state, action) => {
            state.errorMessage = action.payload;
            state.successMessage = '';
        },

        successMessageSet: (state, action) => {
            state.successMessage = action.payload;
            state.errorMessage = '';
        },

        versionLoaded: (state, action) => {
            state.version = action.payload;
        }
    }
});

export const loadDocumentAuditData = (documentId, field) => {
    const encodedField = encodeURIComponent(field);
    return apiCallBegan({
        url: getDocumentURL() + `rest/document/audit/${documentId}/${encodedField}`,
        onStart: documentAuditDataLoading.type,
        onSuccess: documentAuditDataLoaded.type,
        successMessage: `Document entry has been searched`,
        onError: documentAuditDataLoadingFailure.type
    });
};

export const addParticipantDocuments = (participantId, data, msg) =>
    apiUpload({
        url: getDocumentURL() + `rest/document/participant/${participantId}`,
        method: 'post',
        data,
        onSuccess: participantDocumentsAdded.type,
        successMessage: msg,
        onError: errorOccurred.type,
        onValidationError: validationErrorOccurred.type
    });

export const updateParticipantDocument = (filename, participantId, data, msg) =>
    apiCallBegan({
        url: getDocumentURL() + `rest/document/participant/${participantId}/${filename}/meta`,
        method: 'put',
        data,
        onSuccess: participantDocumentDetailsUpdated.type,
        successMessage: msg,
        onError: errorOccurred.type
    });

export const loadParticipantDocumentDetails = (
    participantId,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'dateCreated',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getDocumentURL() +
            `rest/document/participant/${participantId}?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        onStart: participantDocumentDetailsLoading.type,
        onSuccess: participantDocumentDetailsLoaded.type,
        onError: participantDocumentDetailsLoadingFailed.type
    });

export const searchDocumentDetails = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'dateCreated',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getDocumentURL() +
            `rest/document/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: documentDetailsSearchLoading.type,
        onSuccess: documentDetailsSearchLoaded.type,
        onError: documentDetailsSearchLoadingFailure.type
    });

export const downloadParticipantDocument = (participantId, filename) =>
    apiDownload({
        url: getDocumentURL() + `rest/document/participant/${participantId}/${filename}`,
        filename,
        successMessage: `${filename} has been downloaded`,
        onError: errorOccurred.type
    });

export const downloadParticipantDocumentById = (participantId, documentId, filename) =>
    apiDownload({
        url: getDocumentURL() + `rest/document/participant/${participantId}/document/${documentId}`,
        filename,
        successMessage: `${filename} has been downloaded`,
        onError: errorOccurred.type
    });

export const deleteParticipantDocumentDetail = (participantId, filename) =>
    apiCallBegan({
        url: getDocumentURL() + `rest/document/participant/${participantId}/${filename}`,
        method: 'delete',

        successMessage: `${filename} has been deleted`,
        onError: errorOccurred.type
    });

// CSVs
export const addClaimQueueProceedCsv = (data, successMessage) =>
    apiUpload({
        url: getParticipantURL() + `rest/csv/claim-queue/proceed`,
        method: 'post',
        data,
        onSuccess: csvDocumentAdded.type,
        successMessage,
        onError: addDocumentFailure.type
    });

export const addClaimQueueCreatePaidCsv = (data, successMessage) =>
    apiUpload({
        url: getParticipantURL() + `rest/csv/claim-queue/create-paid`,
        method: 'post',
        data,
        onSuccess: csvDocumentAdded.type,
        successMessage,
        onError: addDocumentFailure.type
    });

export const addClaimQueueCreateCsv = (data, successMessage) =>
    apiUpload({
        url: getParticipantURL() + `rest/csv/claim-queue/create`,
        method: 'post',
        data,
        onSuccess: csvDocumentAdded.type,
        successMessage,
        onError: addDocumentFailure.type
    });

export const addParticipantCsv = (data, successMessage) =>
    apiUpload({
        url: getParticipantURL() + `rest/csv/participant`,
        method: 'post',
        data,
        onSuccess: csvDocumentAdded.type,
        successMessage,
        onError: addBatchDocumentFailure.type
    });

export const addPostcodeCsv = (contractId, data, successMessage) =>
    apiUpload({
        url: getParticipantURL() + `rest/csv/postcode-service-team/${contractId}`,
        method: 'post',
        data,
        onSuccess: csvDocumentAdded.type,
        successMessage,
        onError: addDocumentFailure.type
    });

export const addWebsiteCsv = (data, successMessage) =>
    apiUpload({
        url: getParticipantURL() + 'rest/csv/referral',
        method: 'post',
        data,
        onSuccess: csvDocumentAdded.type,
        successMessage,
        onError: addDocumentFailure.type
    });

export const deletePostcodeCsvData = (contractId, successMessage) =>
    apiUpload({
        url: getParticipantURL() + `rest/csv/postcode-service-team/${contractId}`,
        method: 'delete',
        successMessage,
        onError: errorOccurred.type
    });

export const loadDocumentServiceVersion = () =>
    apiVersion({
        url: getDocumentURL() + 'rest/version',
        method: 'get',
        onSuccess: versionLoaded.type,
        onError: errorOccurred.type
    });

export const onClearDocumentErrors = () => clearDocumentErrors();
export const deleteFileUploadErrorEntry = (filename) => fileUploadErrorEntryDeleted(filename);
export const onDeleteParticipantDocumentDetail = (id) => participantDocumentDetailDeleted(id);
export const {
    clearDocumentAuditData,
    clearDocumentDetailsSearch,
    clearDocumentUploadReturnData,
    clearParticipantDocumentDetails,
    clearValidationErrors,
    deleteFileUploadErrorEntries
} = slice.actions;

const {
    documentAuditDataLoading,
    documentAuditDataLoaded,
    documentAuditDataLoadingFailure,
    documentDetailsSearchLoading,
    documentDetailsSearchLoaded,
    documentDetailsSearchLoadingFailure,
    addDocumentFailure,
    addBatchDocumentFailure,
    clearDocumentErrors,
    csvDocumentAdded,
    errorOccurred,
    fileUploadErrorEntryDeleted,
    participantDocumentsAdded,
    participantDocumentDetailDeleted,
    participantDocumentDetailsLoading,
    participantDocumentDetailsLoadingFailed,
    participantDocumentDetailsLoaded,
    participantDocumentDetailsUpdated,
    validationErrorOccurred,
    versionLoaded
} = slice.actions;

export default slice.reducer;
